import { clamp, round0 } from './formatters'

export type PackageType = '' | 'edge' | 'advantage' | 'summit'
export type BuildPayType = '' | 'upfront' | 'two'
export type RevenueCadence = 'one-time' | 'monthly' | 'quarterly'
export type IndustryType =
  | ''
  | 'Windows'
  | 'Roofing'
  | 'Solar (Sales)'
  | 'Solar (Vertical)'
  | 'Solar (Community)'
  | 'Pest'
  | 'Fiber'
  | 'Deregulated Energy'
  | 'Hardscaping'
  | 'Landscaping'
  | 'Other'

export interface Inputs {
  companyName: string
  pkg: PackageType
  buildPay: BuildPayType
  industry: IndustryType
  campaigns: number
  monthlyCampaigns?: number[] // Array of 12 numbers for monthly overrides
  adSpend: number
  monthlyAdSpend?: number[] // Array of 12 numbers for monthly ad spend overrides
  cph: number
  ticket: number
  gpm: number // 0-100 in state, converted to decimal for calc
  salesVetMonthly: number
  salesFirst90Total: number
  pctLaunched: number // 0-100
  pct30: number // 0-100
  pct90: number // 0-100 Day 30 to Day 90 retention
  pct1y: number // 0-100
  pullthrough: number // 0-100
  revenueCadence: RevenueCadence // New: one-time, monthly, or quarterly
  residualTicket?: number // New: monthly ticket for residual products
  monthlyAttrition?: number // New: monthly customer attrition (0-100)
  quarterlyAttrition?: number // New: quarterly customer attrition (0-100)
  monthlyBuildFees?: number[] // Array of 13 numbers for build fee overrides (Build + 12 months)
}

export interface IndustryDefaults {
  ticket: number
  gpm: number
  salesFirst90Total: number
  salesVetMonthly: number
  revenueCadence: RevenueCadence
  residualTicket?: number
}

export const INDUSTRY_DEFAULTS: Record<IndustryType, IndustryDefaults> = {
  '': {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Windows: {
    ticket: 20000,
    gpm: 30,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Roofing: {
    ticket: 20000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Sales)': {
    ticket: 40000,
    gpm: 10,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Vertical)': {
    ticket: 40000,
    gpm: 20,
    salesFirst90Total: 6,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  'Solar (Community)': {
    ticket: 700,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 350,
  },
  Pest: {
    ticket: 170,
    gpm: 40,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 85,
  },
  Fiber: {
    ticket: 100,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 50,
  },
  'Deregulated Energy': {
    ticket: 70,
    gpm: 50,
    salesFirst90Total: 60,
    salesVetMonthly: 45,
    revenueCadence: 'monthly',
    residualTicket: 35,
  },
  Hardscaping: {
    ticket: 20000,
    gpm: 25,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
  Landscaping: {
    ticket: 50000,
    gpm: 30,
    salesFirst90Total: 5,
    salesVetMonthly: 8,
    revenueCadence: 'one-time',
  },
  Other: {
    ticket: 25000,
    gpm: 20,
    salesFirst90Total: 10,
    salesVetMonthly: 6,
    revenueCadence: 'one-time',
  },
}

export interface PackagePreset {
  name: string
  buildTwoPayments: number
  buildUpfront: number
  systemMonthly: number
  campaignMonthly: number
  bonusLaunch: number
  bonusDay30: number
  bonusDay60: number
  bonusDay90: number
}

export const PACKAGE_PRESETS: Record<PackageType, PackagePreset> = {
  '': {
    name: 'No Package Selected',
    buildTwoPayments: 0,
    buildUpfront: 0,
    systemMonthly: 0,
    campaignMonthly: 0,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  edge: {
    name: 'HIRE Edge',
    buildTwoPayments: 3000,
    buildUpfront: 3000,
    systemMonthly: 1500,
    campaignMonthly: 1500,
    bonusLaunch: 0,
    bonusDay30: 0,
    bonusDay60: 0,
    bonusDay90: 0,
  },
  advantage: {
    name: 'HIRE Advantage',
    buildTwoPayments: 3000,
    buildUpfront: 5000,
    systemMonthly: 2500,
    campaignMonthly: 2500,
    bonusLaunch: 250,
    bonusDay30: 250,
    bonusDay60: 250,
    bonusDay90: 250,
  },
  summit: {
    name: 'HIRE Summit',
    buildTwoPayments: 3000,
    buildUpfront: 5000,
    systemMonthly: 2500,
    campaignMonthly: 5500,
    bonusLaunch: 250,
    bonusDay30: 250,
    bonusDay60: 250,
    bonusDay90: 250,
  },
}

export interface RowData {
  period: string
  activeCampaigns: number
  hires: number
  launched: number
  active30d: number
  active60d: number
  active90d: number
  veterans: number
  buildFee: number
  platformFee: number
  campaignFee: number
  hireCost: number
  adSpend: number
  bonuses: number
  totalCost: number
  totalSales: number
  realizationRate: number
  grossRevenue: number
  grossProfit: number
  netProfit: number
  cumProfit: number
  residualRevenue?: number
  totalResidualRevenue?: number
}

export interface CalculationResult {
  preset: PackagePreset
  hiresPerMonth: number
  launchedPerMonth: number
  gpPerSale: number
  totalHireCost: number
  totalAdSpend: number
  totalBonuses: number
  totalCostAll: number
  totalGrossProfit: number
  totalNetProfit: number
  roi: number
  breakeven: string
  rows: RowData[]
}

export const DEFAULT_INPUTS: Inputs = {
  companyName: '',
  pkg: '',
  buildPay: 'upfront',
  industry: '',
  campaigns: 1,
  monthlyCampaigns: Array(12).fill(1),
  adSpend: 5000,
  monthlyAdSpend: Array(12).fill(5000),
  cph: 350,
  ticket: 20000,
  gpm: 30,
  salesVetMonthly: 6,
  salesFirst90Total: 10,
  pctLaunched: 70,
  pct30: 30,
  pct90: 70,
  pct1y: 30,
  pullthrough: 80,
  revenueCadence: 'one-time',
  residualTicket: undefined,
  monthlyAttrition: 2,
  quarterlyAttrition: 5,
  monthlyBuildFees: undefined,
}

export function calculateModel(inputs: Inputs): CalculationResult {
  const months = 12
  const preset = PACKAGE_PRESETS[inputs.pkg]

  const gpm = clamp(inputs.gpm / 100, 0, 1)
  const pctLaunched = clamp(inputs.pctLaunched / 100, 0, 1)
  const pct30 = clamp(inputs.pct30 / 100, 0, 1)
  const pct90 = clamp(inputs.pct90 / 100, 0, 1)
  const pct1y = clamp(inputs.pct1y / 100, 0, 1)
  const pullthrough = clamp(inputs.pullthrough / 100, 0, 1)

  const pct60 = Math.sqrt(pct90)

  const monthlyCampaigns =
    inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12
      ? inputs.monthlyCampaigns
      : Array(12).fill(Math.max(1, inputs.campaigns))

  const monthlyAdSpendValues =
    inputs.monthlyAdSpend && inputs.monthlyAdSpend.length === 12
      ? inputs.monthlyAdSpend
      : Array(12).fill(Math.max(0, inputs.adSpend))

  const cph = Math.max(1, inputs.cph)

  const hired = monthlyCampaigns.map((campaigns, idx) => {
    const adSpendPerCampaign = monthlyAdSpendValues[idx]
    const totalAdSpend = adSpendPerCampaign * campaigns
    return round0(totalAdSpend / cph)
  })

  const launched = hired.map((h) => h * pctLaunched)

  const cohortDay30 = launched.map((x) => x * pct30)
  const cohortDay60 = cohortDay30.map((x) => x * pct60)
  const cohortDay90 = cohortDay60.map((x) => x * pct60)

  const active30d = Array.from({ length: months }, (_, m) => {
    return m >= 1 ? cohortDay30[m - 1] : 0
  })

  const active60d = Array.from({ length: months }, (_, m) => {
    return m >= 2 ? cohortDay60[m - 2] : 0
  })

  const active90d = Array.from({ length: months }, (_, m) => {
    return m >= 3 ? cohortDay90[m - 3] : 0
  })

  const monthlyVeteranRetentionRate = Math.pow(pct1y, 1 / 9)

  const veterans: number[] = []
  for (let m = 0; m < months; m++) {
    if (m <= 3) {
      veterans.push(0)
    } else {
      const previousVeterans = veterans[m - 1] * monthlyVeteranRetentionRate
      const newGraduates = m >= 4 ? cohortDay90[m - 4] : 0
      veterans.push(previousVeterans + newGraduates)
    }
  }

  const salesPer30Days = Math.max(0, inputs.salesFirst90Total) / 3

  const totalSales = Array.from({ length: months }, (_, m) => {
    let sales = 0

    if (m >= 0) {
      const eventualGrads = cohortDay90[m]
      sales += eventualGrads * salesPer30Days
    }

    if (m >= 1) {
      const eventualGrads = cohortDay90[m - 1]
      sales += eventualGrads * salesPer30Days
    }

    if (m >= 2) {
      const eventualGrads = cohortDay90[m - 2]
      sales += eventualGrads * salesPer30Days
    }

    if (m >= 3) {
      for (let cohortMonth = 0; cohortMonth <= m - 3; cohortMonth++) {
        let veteranCount = cohortDay90[cohortMonth]
        const monthsPast90 = m - cohortMonth - 3
        for (let ageMonth = 0; ageMonth < monthsPast90; ageMonth++) {
          veteranCount *= monthlyVeteranRetentionRate
        }
        sales += veteranCount * Math.max(0, inputs.salesVetMonthly)
      }
    }

    return sales
  })

  const realizedSales = totalSales.map((x) => x * pullthrough)

  const residualRevenue: number[] = []
  const totalResidualRevenue: number[] = []

  if (inputs.revenueCadence === 'monthly' && inputs.residualTicket) {
    const monthlyAttritionRate = clamp(
      (inputs.monthlyAttrition || 2) / 100,
      0,
      1,
    )
    const customerRetention = 1 - monthlyAttritionRate

    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let monthlyResidual = 0

      if (m >= 1) {
        for (let sourceMonth = 0; sourceMonth <= m - 1; sourceMonth++) {
          const monthsGeneratingResidual = m - sourceMonth - 1
          const retentionFactor = Math.pow(
            customerRetention,
            monthsGeneratingResidual,
          )
          monthlyResidual +=
            (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
            retentionFactor *
            inputs.residualTicket
        }
      }

      residualRevenue.push(monthlyResidual)
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + monthlyResidual,
      )
    }
  } else if (inputs.revenueCadence === 'quarterly' && inputs.residualTicket) {
    const quarterlyAttritionRate = clamp(
      (inputs.quarterlyAttrition || 5) / 100,
      0,
      1,
    )
    const quarterlyRetention = 1 - quarterlyAttritionRate

    const newMonthlyRevenue = realizedSales.map(
      (sales) => sales * Math.max(0, inputs.ticket),
    )

    for (let m = 0; m < months; m++) {
      let quarterlyResidual = 0

      if (m >= 3) {
        const sourceMonth = m - 3
        quarterlyResidual =
          (newMonthlyRevenue[sourceMonth] / Math.max(0, inputs.ticket)) *
          quarterlyRetention *
          inputs.residualTicket
      }

      residualRevenue.push(quarterlyResidual)
      totalResidualRevenue.push(
        realizedSales[m] * Math.max(0, inputs.ticket) + quarterlyResidual,
      )
    }
  } else {
    for (let m = 0; m < months; m++) {
      residualRevenue.push(0)
      totalResidualRevenue.push(realizedSales[m] * Math.max(0, inputs.ticket))
    }
  }

  const grossRevenue = totalResidualRevenue
  const grossProfit = grossRevenue.map((x) => x * gpm)
  const gpPerSale = Math.max(0, inputs.ticket) * gpm

  // Default build fee for Build row (index 0 in rows)
  const defaultBuildLaunch = preset.buildUpfront

  // Check for custom build fees
  const customBuildFees = inputs.monthlyBuildFees

  // Build row fee - use custom if set, otherwise use default
  const buildRowFee =
    customBuildFees &&
    customBuildFees[0] !== undefined &&
    customBuildFees[0] !== null
      ? customBuildFees[0]
      : defaultBuildLaunch

  const systemMonthly = preset.systemMonthly

  const campaignFees = monthlyCampaigns.map((c) => preset.campaignMonthly * c)

  const bonusLaunch = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 1
      return cohortIdx >= 0 ? preset.bonusLaunch * launched[cohortIdx] : 0
    })

  const bonusDay30 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 2
      return cohortIdx >= 0 ? preset.bonusDay30 * cohortDay30[cohortIdx] : 0
    })

  const bonusDay60 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 3
      return cohortIdx >= 0 ? preset.bonusDay60 * cohortDay60[cohortIdx] : 0
    })

  const bonusDay90 = Array(months)
    .fill(0)
    .map((_, m) => {
      const cohortIdx = m - 4
      return cohortIdx >= 0 ? preset.bonusDay90 * cohortDay90[cohortIdx] : 0
    })

  const bonuses = bonusLaunch.map(
    (x, i) => x + bonusDay30[i] + bonusDay60[i] + bonusDay90[i],
  )

  // Monthly build fees - use custom if set, otherwise default to 0 for all months
  const buildFees = Array(months)
    .fill(0)
    .map((_, i) => {
      // Check for custom build fee (index i+1 because index 0 is Build row)
      if (
        customBuildFees &&
        customBuildFees[i + 1] !== undefined &&
        customBuildFees[i + 1] !== null
      ) {
        return customBuildFees[i + 1]
      }
      // Default: no build fees in monthly rows
      return 0
    })

  // Platform fees - set to 0 if active campaigns is 0 for that month
  const platformFees = monthlyCampaigns.map((campaigns) =>
    campaigns === 0 ? 0 : systemMonthly,
  )

  const hireCost = Array(months)
    .fill(0)
    .map((_, i) => buildFees[i] + platformFees[i] + campaignFees[i])

  const adSpend = monthlyCampaigns.map(
    (c, idx) => monthlyAdSpendValues[idx] * c,
  )

  const totalCost = Array(months)
    .fill(0)
    .map((_, i) => hireCost[i] + adSpend[i] + bonuses[i])
  const netProfit = grossProfit.map((x, i) => x - totalCost[i])

  const rows: RowData[] = []
  let cum = -buildRowFee

  rows.push({
    period: 'Build',
    activeCampaigns: 0,
    hires: 0,
    launched: 0,
    active30d: 0,
    active60d: 0,
    active90d: 0,
    veterans: 0,
    buildFee: buildRowFee,
    platformFee: 0,
    campaignFee: 0,
    hireCost: buildRowFee,
    adSpend: 0,
    bonuses: 0,
    totalCost: buildRowFee,
    totalSales: 0,
    realizationRate: pullthrough,
    grossRevenue: 0,
    grossProfit: 0,
    netProfit: -buildRowFee,
    cumProfit: cum,
    residualRevenue: 0,
    totalResidualRevenue: 0,
  })

  for (let i = 0; i < months; i++) {
    cum += netProfit[i]
    rows.push({
      period: `Month ${i + 1}`,
      activeCampaigns: monthlyCampaigns[i],
      hires: hired[i],
      launched: launched[i],
      active30d: active30d[i],
      active60d: active60d[i],
      active90d: active90d[i],
      veterans: veterans[i],
      buildFee: buildFees[i],
      platformFee: platformFees[i],
      campaignFee: campaignFees[i],
      hireCost: hireCost[i],
      adSpend: adSpend[i],
      bonuses: bonuses[i],
      totalCost: totalCost[i],
      totalSales: totalSales[i],
      realizationRate: pullthrough,
      grossRevenue: grossRevenue[i],
      grossProfit: grossProfit[i],
      netProfit: netProfit[i],
      cumProfit: cum,
      residualRevenue: residualRevenue[i],
      totalResidualRevenue: totalResidualRevenue[i],
    })
  }

  const totalHireCost = buildRowFee + hireCost.reduce((a, b) => a + b, 0)
  const totalAdSpend = adSpend.reduce((a, b) => a + b, 0)
  const totalBonuses = bonuses.reduce((a, b) => a + b, 0)
  const totalCostAll = totalHireCost + totalAdSpend + totalBonuses
  const totalGrossProfit = grossProfit.reduce((a, b) => a + b, 0)
  const totalNetProfit =
    totalGrossProfit -
    (hireCost.reduce((a, b) => a + b, 0) + totalAdSpend + totalBonuses) -
    buildRowFee
  const roi = totalCostAll > 0 ? (totalNetProfit / totalCostAll) * 100 : 0

  let breakeven = 'Not within 12 months'
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].cumProfit >= 0) {
      breakeven = rows[i].period
      break
    }
  }

  return {
    preset,
    hiresPerMonth: hired[0],
    launchedPerMonth: launched[0],
    gpPerSale,
    totalHireCost,
    totalAdSpend,
    totalBonuses,
    totalCostAll,
    totalGrossProfit,
    totalNetProfit,
    roi,
    breakeven,
    rows,
  }
}
